import React, { useEffect, useState, useMemo } from "react";
import { useConsultantVisibleContext } from "../../../features/consultant/screenContextAdapter";
import "./PassiveLoad.css";

import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FormattedMessage } from "react-intl";

import DragDropCompanies from "../../../components/Measurement/DragDropCompanies";
import DragDropDevices from "../../../components/Measurement/DragDropDevices";
import GroupSelector from "../../../components/Measurement/GroupSelector";
import FrequencyComponent from "../../../components/FilterFrequency/FilterFrequency";
import PassiveLoadTable from "./PassiveLoadTable";
import PassiveNightChart from "./PassiveLoadChart";
import PassiveBaseLoadChart from "./PassiveBaseLoadChart";
import PassiveSavingsTable from "./PassiveSavingsTable";
import PassiveAnomaliesTable from "./PassiveAnomaliesTable";
import PassiveStatisticsTable from "./PassiveStatisticsTable";
import PassiveModelMetrics from "./PassiveModelMetrics";
import PassiveRecommendations from "./PassiveRecommendations";
import {
  getPassiveLevel,
  calculatePassiveStatistics,
  calculateSimplePassiveAnomalies,
  calculateExcessProbability,
  buildSimplePredictiveSeries,
  calculateSimpleModelMetrics,
} from "./passiveLoadHelpers";

import MeasurementContext from "../../../context/MeasurementContext";
import { useMeasurementGroups } from "../../../hooks/useMeasurementGroups";

import { getPassiveLoadData } from "../../../redux/apiCalls/analysisCall/passiveLoadCall";
import { createPassiveLoadException } from "../../../redux/apiCalls/analysisCall/passiveLoadConfigCall";
import PassiveLoadConfig from "./PassiveLoadConfig";

import { getInitialDatesFromStorageOrToday } from "../../../helpers/measurement-secGroup";

import { Check } from "lucide-react";
import exportPassiveLoadExcel from "./exportPassiveLoadExcel";

const DEFAULT_TIME_ZONE = "America/Argentina/Buenos_Aires";

const ALLOWED_CONFIG_PRIVILEGES = ["master", "admin", "manager"];

const normalizePrivilege = (value) => String(value || "").toLowerCase().trim();

const InfoIcon = ({ text }) => (
  <span className="metric-info-icon" title={text} aria-label={text}>
    <i className="bi bi-info-circle-fill" />
  </span>
);

const TitleWithInfo = ({ children, info }) => (
  <span className="title-with-info">
    <span>{children}</span>
    <InfoIcon text={info} />
  </span>
);

const getDeviceData = (device) => device?.data || device || {};

const getDeviceMac = (device) => {
  const data = getDeviceData(device);

  return (
    device?.mac ||
    device?.serial_number ||
    data?.mac ||
    data?.serial_number ||
    null
  );
};

const getDeviceBranchId = (device) => {
  const data = getDeviceData(device);

  return (
    device?.branch_fk ??
    device?.branchId ??
    device?.branch_id ??
    data?.branch_fk ??
    data?.branchId ??
    data?.branch_id ??
    null
  );
};

const getDeviceCompanyId = (device) => {
  const data = getDeviceData(device);

  return (
    device?.company_fk ??
    device?.companyId ??
    device?.company_id ??
    data?.company_fk ??
    data?.companyId ??
    data?.company_id ??
    null
  );
};

const normalizeWarningMessage = (item) => {
  if (item === null || item === undefined) return "";

  if (typeof item === "string") return item.trim();

  if (typeof item === "object") {
    const message =
      item.message ||
      item.text ||
      item.description ||
      item.warning ||
      item.detail ||
      "";

    if (message) return String(message).trim();

    try {
      const serialized = JSON.stringify(item);
      return serialized === "{}" ? "" : serialized;
    } catch (error) {
      return "";
    }
  }

  return String(item).trim();
};

const normalizeComparableMessage = (message) =>
  String(message || "")
    .toLowerCase()
    .replace(/\d+/g, "#")
    .replace(/\s+/g, " ")
    .trim();

const isScheduleWarningMessage = (message) => {
  const normalized = String(message || "").toLowerCase();

  return (
    normalized.includes("horario operativo") ||
    normalized.includes("no tiene horario") ||
    normalized.includes("no se detectó horario") ||
    normalized.includes("sin horario operativo") ||
    normalized.includes("alcance completo")
  );
};

const isSuggestedOvertimeWarningMessage = (message) => {
  const normalized = String(message || "").toLowerCase();

  return (
    normalized.includes("posible hora extra") ||
    normalized.includes("hora extra") ||
    normalized.includes("consumo elevado fuera del horario")
  );
};

const getIntervalKey = (interval) => {
  return `${interval?.start || ""}_${interval?.end || ""}`;
};

const getSuggestedOvertimeGroupKey = (interval) => {
  return `${interval?.start || ""}_${interval?.end || ""}`;
};

const getIntervalDeviceLabel = (interval) => {
  return (
    interval?.device_label ||
    interval?.device_name ||
    interval?.deviceName ||
    interval?.device_mac ||
    interval?.mac ||
    null
  );
};

const groupSuggestedOvertimeIntervals = (intervals = []) => {
  const map = new Map();

  intervals.forEach((interval) => {
    if (!interval?.start || !interval?.end) return;

    const key = getSuggestedOvertimeGroupKey(interval);
    const deviceLabel = getIntervalDeviceLabel(interval);

    if (!map.has(key)) {
      map.set(key, {
        ...interval,
        key,
        deviceLabels: [],
        intervalCount: 0,
        totalEnergy: 0,
      });
    }

    const grouped = map.get(key);

    grouped.intervalCount += 1;
    grouped.totalEnergy += Number(interval.totalEnergy) || 0;

    if (deviceLabel && !grouped.deviceLabels.includes(deviceLabel)) {
      grouped.deviceLabels.push(deviceLabel);
    }
  });

  return Array.from(map.values()).map((interval) => ({
    ...interval,
    deviceSummary:
      interval.deviceLabels.length === 0
        ? null
        : interval.deviceLabels.length === 1
          ? interval.deviceLabels[0]
          : `${interval.deviceLabels.length} dispositivos`,
    totalEnergy: Number(interval.totalEnergy.toFixed(3)),
  }));
};

const formatLocalDateTime = (value, timeZone = DEFAULT_TIME_ZONE) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleString("es-AR", {
    timeZone,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
};

const formatLocalTime = (value, timeZone = DEFAULT_TIME_ZONE) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleTimeString("es-AR", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
};

const getLocalDateAndTime = (value, timeZone = DEFAULT_TIME_ZONE) => {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return {
      date: "",
      time: "",
    };
  }

  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  })
    .formatToParts(date)
    .reduce((acc, part) => {
      if (part.type !== "literal") {
        acc[part.type] = part.value;
      }

      return acc;
    }, {});

  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    time: `${parts.hour}:${parts.minute}`,
  };
};

const PassiveLoad = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const currentUser = useSelector((state) => state.user.currentUser);

  const userAccessToken = currentUser?.accessToken;

  const privilege = normalizePrivilege(
    currentUser?.privilege ||
      currentUser?.role ||
      currentUser?.user?.privilege ||
      currentUser?.user?.role
  );

  const canConfigurePassiveLoad = ALLOWED_CONFIG_PRIVILEGES.includes(privilege);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [frec, setFrec] = useState("hour");

  const [firstSelect, setFirstSelect] = useState([]);
  const [devicesSelected, setDevicesSelected] = useState([]);

  const [meterSelected, setMeterSelected] = useState(null);

  const [openDropdown, setOpenDropdown] = useState({});
  const [showConfig, setShowConfig] = useState(false);

  const [appliedState, setAppliedState] = useState(null);
  useConsultantVisibleContext({ ...(appliedState || { firstSelect, devicesSelected, startDate, endDate }),
    selected_metric: meterSelected?.label || meterSelected?.tag, selected_unit: meterSelected?.unit,
    selected_chart: "Carga pasiva" });

  const [confirmingOvertimeKey, setConfirmingOvertimeKey] = useState(null);
  const [ignoredOvertimeKeys, setIgnoredOvertimeKeys] = useState([]);
  const [confirmedOvertimeKeys, setConfirmedOvertimeKeys] = useState([]);
  const [overtimeFeedback, setOvertimeFeedback] = useState(null);

  const { allGroups, primary, setPrimary, second, setSecond } =
    useMeasurementGroups(dispatch, userAccessToken, { firstSelect });

  const contextValue = useMemo(
    () => ({ meterSelected, setMeterSelected }),
    [meterSelected]
  );

  useEffect(() => {
    if (!canConfigurePassiveLoad && showConfig) {
      setShowConfig(false);
    }
  }, [canConfigurePassiveLoad, showConfig]);

  useEffect(() => {
    const { startDate: initialStart, endDate: initialEnd } =
      getInitialDatesFromStorageOrToday();

    setStartDate(initialStart);
    setEndDate(initialEnd);

    try {
      const savedStr = localStorage.getItem("passiveLoad_state");

      if (savedStr) {
        const saved = JSON.parse(savedStr);

        if (saved.frec) setFrec(saved.frec);

        if (Array.isArray(saved.devicesSelected)) {
          setDevicesSelected(saved.devicesSelected);
        }

        if (saved.primary) setPrimary(saved.primary);
        if (saved.secondary) setSecond(saved.secondary);
      }
    } catch (e) {
      console.error("Error restaurando passiveLoad_state", e);
    }
  }, [setPrimary, setSecond]);

  const selectedBranchId = useMemo(() => {
    if (!devicesSelected?.length) return null;

    const branchIds = devicesSelected
      .map((device) => getDeviceBranchId(device))
      .filter((id) => id != null);

    return branchIds.length > 0 && branchIds.every((id) => id === branchIds[0])
      ? branchIds[0]
      : null;
  }, [devicesSelected]);

  const selectedCompanyId = useMemo(() => {
    if (!devicesSelected?.length) return null;

    const companyIds = devicesSelected
      .map((device) => getDeviceCompanyId(device))
      .filter((id) => id != null);

    return companyIds.length > 0 &&
      companyIds.every((id) => id === companyIds[0])
      ? companyIds[0]
      : null;
  }, [devicesSelected]);

  const activeBranchId = useMemo(() => {
    return (
      selectedBranchId ??
      data?.configurationBranchId ??
      data?.branch_id ??
      data?.branchId ??
      null
    );
  }, [selectedBranchId, data]);

  const activeCompanyId = useMemo(() => {
    return (
      selectedCompanyId ??
      data?.configurationCompanyId ??
      data?.company_id ??
      data?.companyId ??
      null
    );
  }, [selectedCompanyId, data]);

  const fetchPassiveLoad = async () => {
    if (!devicesSelected.length || !startDate || !endDate) return;

    try {
      setLoading(true);

      const macs = devicesSelected.map((d) => getDeviceMac(d)).filter(Boolean);

      const payload = {
        startDate,
        endDate,
        frec,
        devicesSelected,
        primary,
        secondary: second,
      };

      localStorage.setItem("passiveLoad_state", JSON.stringify(payload));

      setAppliedState({
        startDate,
        endDate,
        frec,
        devicesSelected,
        primary,
        secondary: second,
      });

      const response = await getPassiveLoadData(
        dispatch,
        userAccessToken,
        macs,
        startDate,
        endDate,
        frec,
        selectedBranchId,
        selectedCompanyId
      );

      setData(response);
    } catch (err) {
      console.error("Error loading passive load:", err);

      setData({
        series: [],
        analysisSeries: [],
        totals: null,
        warnings: ["No se pudo cargar el análisis de carga pasiva."],
      });
    } finally {
      setLoading(false);
    }
  };

  const hasChanges = useMemo(() => {
    if (!appliedState) return true;

    return (
      appliedState.frec !== frec ||
      appliedState.startDate?.toString() !== startDate?.toString() ||
      appliedState.endDate?.toString() !== endDate?.toString() ||
      JSON.stringify(appliedState.devicesSelected) !==
        JSON.stringify(devicesSelected)
    );
  }, [frec, startDate, endDate, devicesSelected, appliedState]);

  const allSeries = useMemo(() => {
    return Array.isArray(data?.series) ? data.series : [];
  }, [data]);

  const analysisSeries = useMemo(() => {
    if (data?.scheduleConfigured === false) {
      return allSeries;
    }

    return Array.isArray(data?.analysisSeries) && data.analysisSeries.length > 0
      ? data.analysisSeries
      : allSeries;
  }, [data?.scheduleConfigured, data?.analysisSeries, allSeries]);

  const hasSeriesData = analysisSeries.length > 0;

  const analysisData = useMemo(() => {
    return {
      ...data,
      series: analysisSeries,
    };
  }, [data, analysisSeries]);

  const passiveStatistics = useMemo(() => {
    if (!hasSeriesData) return null;

    return data?.statistics ?? calculatePassiveStatistics(analysisSeries);
  }, [analysisSeries, data?.statistics, hasSeriesData]);

  const passiveAnomalies = useMemo(() => {
    if (!hasSeriesData) return [];

    if (Array.isArray(data?.anomalies) && data.anomalies.length > 0) {
      return data.anomalies;
    }

    const anomaliesFromSeries = analysisSeries
      .filter((point) => {
        const level =
          point?.anomaly_level || point?.classification || point?.clasificacion;

        const backendSaysAnomaly =
          level && String(level).toLowerCase() !== "normal";

        const elevatedNonProductive =
          point?.is_non_productive === true &&
          point?.is_near_base_load === false;

        return backendSaysAnomaly || elevatedNonProductive;
      })
      .map((point) => {
        const realValue =
          point.total_energy ?? point.energy ?? point.passive_energy ?? 0;

        const expectedValue =
          point.expected_energy ??
          point.estimated_energy ??
          passiveStatistics?.median ??
          data?.baseLoad ??
          0;

        const level =
          point.anomaly_level || point.classification || point.clasificacion;

        const classification =
          level && String(level).toLowerCase() !== "normal"
            ? level
            : "Revisar";

        return {
          timestamp: point.timestamp,
          local_timestamp: point.local_timestamp,
          realValue,
          expectedValue,
          difference: realValue - expectedValue,
          zScore: point.z_score ?? point.zScore ?? 0,
          residualStd: point.residual_std ?? point.residualStd ?? null,
          classification,
          source: point.source || "statistical",
          passiveReason: point.passive_reason,
        };
      });

    if (anomaliesFromSeries.length > 0) {
      return anomaliesFromSeries;
    }

    return calculateSimplePassiveAnomalies(analysisSeries, passiveStatistics);
  }, [
    data?.anomalies,
    analysisSeries,
    passiveStatistics,
    data?.baseLoad,
    hasSeriesData,
  ]);

  const excessProbability = useMemo(() => {
    if (!hasSeriesData || !passiveStatistics) return null;

    return calculateExcessProbability(analysisSeries, passiveStatistics);
  }, [analysisSeries, passiveStatistics, hasSeriesData]);

  const predictiveSeries = useMemo(() => {
    if (!hasSeriesData || !passiveStatistics) return [];

    return buildSimplePredictiveSeries(analysisSeries, passiveStatistics);
  }, [analysisSeries, passiveStatistics, hasSeriesData]);

  const modelMetrics = useMemo(() => {
    if (!hasSeriesData) return null;

    return data?.modelMetrics ?? calculateSimpleModelMetrics(predictiveSeries);
  }, [predictiveSeries, data?.modelMetrics, hasSeriesData]);

  const handleExportExcel = () => {
    if (!hasSeriesData) return;

    exportPassiveLoadExcel({
      data: analysisData,
      passiveStatistics,
      passiveAnomalies,
      modelMetrics,
      metadata: {
        startDate,
        endDate,
        frec,
        devices: devicesSelected
          ?.map((d) => d.name || d.label || getDeviceMac(d))
          .filter(Boolean)
          .join(", "),
      },
    });
  };

  const passivePct = data?.totals?.passivePercentage ?? null;

  const passivePctDisplay =
    passivePct != null && !isNaN(Number(passivePct))
      ? `${Number(passivePct).toFixed(2)}%`
      : "-";

  const baseLoadValue =
    data?.baseLoad ??
    data?.totals?.baseLoadEnergy ??
    data?.totals?.baseLoad ??
    null;

  const baseLoadUnit =
    data?.baseLoadUnit ?? (data?.baseLoad != null ? "kW" : "kWh");

  const baseLoadDisplay = Number.isFinite(Number(baseLoadValue))
    ? `${Number(baseLoadValue).toFixed(2)} ${baseLoadUnit}`
    : "-";

  const showEmptyState = data && !loading && !hasSeriesData;

  const warningItems = useMemo(() => {
    if (!Array.isArray(data?.warnings)) return [];

    return data.warnings
      .map((item) => normalizeWarningMessage(item))
      .filter(Boolean);
  }, [data?.warnings]);

  const suggestedOvertimeIntervals = useMemo(() => {
    const intervals = data?.suggestedOvertime?.intervals;

    if (!Array.isArray(intervals)) return [];

    const filtered = intervals
      .filter((interval) => interval?.start && interval?.end)
      .filter((interval) => {
        const key = getIntervalKey(interval);

        return (
          !ignoredOvertimeKeys.includes(key) &&
          !confirmedOvertimeKeys.includes(key)
        );
      });

    return groupSuggestedOvertimeIntervals(filtered);
  }, [
    data?.suggestedOvertime?.intervals,
    ignoredOvertimeKeys,
    confirmedOvertimeKeys,
  ]);

  const backendAlreadyWarnedNoSchedule = useMemo(() => {
    return warningItems.some((message) => isScheduleWarningMessage(message));
  }, [warningItems]);

  const scheduleWarningMessage = useMemo(() => {
    if (data?.scheduleConfigured !== false) return null;

    if (backendAlreadyWarnedNoSchedule) return null;

    return "Este dispositivo no tiene horario operativo configurado. El análisis se muestra sobre todo el período seleccionado.";
  }, [data?.scheduleConfigured, backendAlreadyWarnedNoSchedule]);

  const visibleWarningItems = useMemo(() => {
    if (suggestedOvertimeIntervals.length <= 0) return warningItems;

    return warningItems.filter(
      (message) => !isSuggestedOvertimeWarningMessage(message)
    );
  }, [warningItems, suggestedOvertimeIntervals.length]);

  const analysisMessages = useMemo(() => {
    const rawMessages = [scheduleWarningMessage, ...visibleWarningItems]
      .map((message) => String(message || "").trim())
      .filter(Boolean);

    const seen = new Set();

    return rawMessages.filter((message) => {
      const comparable = normalizeComparableMessage(message);

      if (seen.has(comparable)) return false;

      const isScheduleMessageComparable = isScheduleWarningMessage(comparable);

      if (isScheduleMessageComparable && seen.has("schedule-warning")) {
        return false;
      }

      if (isScheduleMessageComparable) {
        seen.add("schedule-warning");
      }

      seen.add(comparable);

      return true;
    });
  }, [scheduleWarningMessage, visibleWarningItems]);

  const timeZone = data?.timeZone || DEFAULT_TIME_ZONE;

  const handleIgnoreSuggestedOvertime = (interval) => {
    const key = getIntervalKey(interval);

    setIgnoredOvertimeKeys((current) =>
      current.includes(key) ? current : [...current, key]
    );

    setOvertimeFeedback(null);
  };

  const handleConfirmSuggestedOvertime = async (interval) => {
    if (!canConfigurePassiveLoad) {
      setOvertimeFeedback({
        type: "error",
        message: "No tenés permisos para guardar horas extra.",
      });
      return;
    }

    const key = getIntervalKey(interval);

    if (!activeBranchId) {
      setOvertimeFeedback({
        type: "error",
        message:
          "No se pudo identificar la sucursal para guardar la hora extra.",
      });
      return;
    }

    const startLocal = getLocalDateAndTime(interval.start, timeZone);
    const endLocal = getLocalDateAndTime(interval.end, timeZone);

    if (!startLocal.date || !startLocal.time || !endLocal.time) {
      setOvertimeFeedback({
        type: "error",
        message: "No se pudo convertir el rango horario de la hora extra.",
      });
      return;
    }

    const payload = {
      company_id: activeCompanyId ?? null,
      branch_id: activeBranchId,
      mac: null,
      exception_date: startLocal.date,
      start_time: startLocal.time,
      end_time: endLocal.time,
      exception_type: "OVERTIME",
      description: `Hora extra sugerida automáticamente (${formatLocalDateTime(
        interval.start,
        timeZone
      )} a ${formatLocalTime(interval.end, timeZone)})`,
      enabled: true,
    };

    try {
      setConfirmingOvertimeKey(key);
      setOvertimeFeedback(null);

      await createPassiveLoadException(userAccessToken, payload);

      setConfirmedOvertimeKeys((current) =>
        current.includes(key) ? current : [...current, key]
      );

      setOvertimeFeedback({
        type: "success",
        message:
          "Hora extra agregada a la sucursal. Se recalculará el análisis.",
      });

      await fetchPassiveLoad();
    } catch (error) {
      console.error("Error confirmando hora extra sugerida:", error);

      setOvertimeFeedback({
        type: "error",
        message: "No se pudo agregar la hora extra a la sucursal.",
      });
    } finally {
      setConfirmingOvertimeKey(null);
    }
  };

  return (
    <MeasurementContext.Provider value={contextValue}>
      <div className="container-heatmap">
        <div className="page-content">
          <div className="cont-path">
            <p className="path path-cursor" onClick={() => navigate(`/`)}>
              <FormattedMessage id="Dashboard" defaultMessage="Escritorio" />
              {"> "}
            </p>
            <p className="path">
              <FormattedMessage
                id="PassiveLoad"
                defaultMessage="Cargas pasivas"
              />
            </p>
          </div>

          <div className="measurement">
            <DragDropCompanies
              setFirstSelect={setFirstSelect}
              firstSelect={firstSelect}
              maxSelections={1}
            />

            <GroupSelector
              allGroups={allGroups}
              primary={primary}
              setPrimary={setPrimary}
              second={second}
              setSecond={setSecond}
              openDropdown={openDropdown}
              setOpenDropdown={setOpenDropdown}
              firstSelect={firstSelect}
            />

            <DragDropDevices
              setDevicesSelected={setDevicesSelected}
              firstSelect={firstSelect}
              primary={primary}
              second={second}
              prefillDevices={devicesSelected}
              showParameters={false}
            />

            <div className="heatmap-row-filter-btn">
              <FrequencyComponent
                onValuesChange={(newFrec, newStart, newEnd) => {
                  setFrec(newFrec);
                  setStartDate(newStart);
                  setEndDate(newEnd);
                }}
                startDate={startDate}
                endDate={endDate}
                frec={frec}
                isAnalysis={true}
              />

              {hasChanges && (
                <button className="heatmap-btn" onClick={fetchPassiveLoad}>
                  <Check size={14} style={{ marginRight: "4px" }} />
                  Aplicar
                </button>
              )}

              {canConfigurePassiveLoad && (
                <button
                  className="heatmap-btn"
                  onClick={() => setShowConfig((s) => !s)}
                  style={{ marginLeft: 8 }}
                  disabled={!devicesSelected?.length}
                >
                  Configurar
                </button>
              )}

              {hasSeriesData && !loading && (
                <button className="heatmap-btn" onClick={handleExportExcel}>
                  Exportar Excel
                </button>
              )}
            </div>

            {canConfigurePassiveLoad && showConfig && (
              <PassiveLoadConfig
                token={userAccessToken}
                devices={devicesSelected}
                onClose={() => setShowConfig(false)}
              />
            )}

            {loading && <p>Cargando...</p>}

            {showEmptyState && (
              <div className="passive-empty-state">
                <h3>Sin datos para el período seleccionado</h3>
                <p>
                  No se encontraron mediciones para los dispositivos elegidos en
                  el rango seleccionado. Probá con otro rango de fechas o
                  verificá que el dispositivo tenga datos cargados.
                </p>
              </div>
            )}

            {hasSeriesData && (
              <>
                {analysisMessages.length > 0 && (
                  <div className="passive-load-analysis-warnings">
                    {analysisMessages.map((message, index) => {
                      const isScheduleMessage =
                        message === scheduleWarningMessage ||
                        isScheduleWarningMessage(message);

                      return (
                        <div
                          className="passive-load-analysis-warning"
                          key={`${message}-${index}`}
                        >
                          <strong>
                            {isScheduleMessage ? "Aviso:" : "Aviso:"}
                          </strong>{" "}
                          {message}
                        </div>
                      );
                    })}
                  </div>
                )}

                {suggestedOvertimeIntervals.length > 0 && (
                  <div className="passive-overtime-box">
                    <div className="passive-overtime-header">
                      <div>
                        <strong>Posibles horas extra detectadas</strong>
                        <p>
                          Se encontraron períodos con consumo elevado fuera del
                          horario normal.
                          {canConfigurePassiveLoad
                            ? " Podés confirmarlos como hora extra para la sucursal o ignorarlos."
                            : " Un usuario con permisos puede confirmarlos como hora extra."}
                        </p>
                      </div>

                      <span className="passive-overtime-count">
                        {suggestedOvertimeIntervals.length}
                      </span>
                    </div>

                    <div
                      className={
                        suggestedOvertimeIntervals.length > 2
                          ? "passive-overtime-list passive-overtime-list-scroll"
                          : "passive-overtime-list"
                      }
                    >
                      {suggestedOvertimeIntervals.map((interval) => {
                        const key = getIntervalKey(interval);
                        const isConfirming = confirmingOvertimeKey === key;

                        return (
                          <div className="passive-overtime-item" key={key}>
                            <div className="passive-overtime-content">
                              <div className="passive-overtime-title">
                                Posible hora extra detectada
                              </div>

                              <div className="passive-overtime-text">
                                Se detectó consumo elevado fuera del horario
                                normal el{" "}
                                <strong>
                                  {formatLocalDateTime(interval.start, timeZone)}
                                </strong>{" "}
                                hasta{" "}
                                <strong>
                                  {formatLocalTime(interval.end, timeZone)}
                                </strong>
                                .
                              </div>

                              {interval.deviceSummary && (
                                <div className="passive-overtime-device">
                                  Dispositivo:{" "}
                                  <strong>{interval.deviceSummary}</strong>
                                </div>
                              )}

                              {interval.totalEnergy > 0 && (
                                <div className="passive-overtime-energy">
                                  Consumo detectado:{" "}
                                  <strong>
                                    {interval.totalEnergy.toFixed(3)} kWh
                                  </strong>
                                </div>
                              )}
                            </div>

                            <div className="passive-overtime-actions">
                              {canConfigurePassiveLoad && (
                                <button
                                  type="button"
                                  className="heatmap-btn"
                                  onClick={() =>
                                    handleConfirmSuggestedOvertime(interval)
                                  }
                                  disabled={isConfirming}
                                >
                                  {isConfirming
                                    ? "Agregando..."
                                    : "Agregar a sucursal"}
                                </button>
                              )}

                              <button
                                type="button"
                                className="heatmap-btn passive-overtime-ignore"
                                onClick={() =>
                                  handleIgnoreSuggestedOvertime(interval)
                                }
                                disabled={isConfirming}
                              >
                                Ignorar
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {overtimeFeedback?.message && (
                      <div
                        className={
                          overtimeFeedback.type === "error"
                            ? "passive-overtime-feedback passive-overtime-feedback-error"
                            : "passive-overtime-feedback passive-overtime-feedback-success"
                        }
                      >
                        <strong>
                          {overtimeFeedback.type === "error"
                            ? "Error:"
                            : "Listo:"}
                        </strong>{" "}
                        {overtimeFeedback.message}
                      </div>
                    )}
                  </div>
                )}

                <div className="passive-kpis">
                  <div className="passive-kpi">
                    <span className="kpi-label">Consumo total</span>
                    <span className="kpi-value">
                      {data?.totals?.totalEnergy} kWh
                    </span>
                  </div>

                  <div className="passive-kpi">
                    <span className="kpi-label">Consumo fuera de horario</span>
                    <span className="kpi-value">
                      {data?.totals?.nonProductiveEnergy ??
                        data?.totals?.passiveNightEnergy}{" "}
                      kWh
                    </span>
                  </div>

                  <div className="passive-kpi">
                    <span className="kpi-label">Carga pasiva base</span>
                    <span className="kpi-value">
                      {data?.totals?.passiveBaseEnergy ??
                        data?.totals?.passiveNightEnergy}{" "}
                      kWh
                    </span>
                  </div>

                  <div className="passive-kpi">
                    <span className="kpi-label">% fuera de horario</span>
                    <span className="kpi-value">{passivePctDisplay}</span>
                  </div>

                  <div className="passive-kpi">
                    <span className="kpi-label">Estado del período</span>
                    <span className="kpi-value">
                      {getPassiveLevel(passivePct)}
                    </span>
                  </div>

                  {excessProbability != null && (
                    <div className="passive-kpi">
                      <span className="kpi-label">Probabilidad de exceso</span>
                      <span className="kpi-value">
                        {(excessProbability * 100).toFixed(2)}%
                      </span>
                    </div>
                  )}

                  {(data?.totals?.elevatedNonProductiveEnergy ?? 0) > 0 && (
                    <div className="passive-kpi">
                      <span className="kpi-label">
                        Consumo elevado fuera de horario
                      </span>
                      <span className="kpi-value">
                        {data?.totals?.elevatedNonProductiveEnergy} kWh
                      </span>
                    </div>
                  )}

                  {(data?.totals?.eventReviewCount ??
                    data?.totals?.anomalyCount ??
                    0) > 0 && (
                    <div className="passive-kpi">
                      <span className="kpi-label">Eventos a revisar</span>
                      <span className="kpi-value">
                        {data?.totals?.eventReviewCount ??
                          data?.totals?.anomalyCount}
                      </span>
                    </div>
                  )}

                  {(data?.totals?.criticalAnomalyCount ?? 0) > 0 && (
                    <div className="passive-kpi">
                      <span className="kpi-label">Anomalías críticas</span>
                      <span className="kpi-value">
                        {data?.totals?.criticalAnomalyCount}
                      </span>
                    </div>
                  )}
                </div>

                <h2>
                  <TitleWithInfo info="Muestra el consumo fuera del horario laboral configurado. Sirve para ver cuánta energía consumen los dispositivos cuando deberían estar apagados, en reposo o sin actividad productiva.">
                    Consumos pasivos nocturnos
                  </TitleWithInfo>
                </h2>

                <PassiveNightChart data={data} fullData={data} />

                <p className="passive-explanation">
                  Energía consumida fuera del horario productivo debido a
                  sistemas conectados cuando no se trabaja en la instalación.
                </p>

                <h2>
                  <TitleWithInfo info="Muestra la línea base del dispositivo: el consumo mínimo o permanente que se mantiene incluso cuando no hay actividad. Sirve para identificar cargas que quedan encendidas de forma constante.">
                    Consumos pasivos permanentes
                  </TitleWithInfo>
                </h2>

                <PassiveBaseLoadChart data={analysisData} />

                <p className="passive-explanation">
                  Representa la línea base de los dispositivos, es decir, el
                  consumo mínimo permanente que existe incluso cuando no hay
                  actividad.
                </p>

                <PassiveLoadTable data={analysisData} />

                <PassiveSavingsTable data={analysisData} />

                <PassiveStatisticsTable statistics={passiveStatistics} />

                <PassiveAnomaliesTable
                  anomalies={passiveAnomalies}
                  data={analysisData}
                />

                {/*
                <PassiveModelMetrics modelMetrics={modelMetrics} />

                <PassiveRecommendations
                  data={analysisData}
                  statistics={passiveStatistics}
                  anomalies={passiveAnomalies}
                />
                */}
              </>
            )}
          </div>
        </div>
      </div>
    </MeasurementContext.Provider>
  );
};

export default PassiveLoad;
